import React from 'react';
import { COLORS, TYPOGRAPHY, SPACING, SHADOWS, RADIUS, TRANSITIONS } from '../constants/designTokens';
import UnifiedCard from './UnifiedCard';

const TeamMessage = () => {
  return (
    <>
      <style>{`
        @keyframes pulsate {
          0%, 100% {
            transform: scale(1);
            box-shadow: ${SHADOWS.xl};
          }
          50% {
            transform: scale(1.05);
            box-shadow: ${SHADOWS['2xl']};
          }
        }

        .team-message-section {
          background: linear-gradient(135deg, ${COLORS.background.light} 0%, ${COLORS.background.lightTeal} 100%);
          position: relative;
          overflow: hidden;
        }

        .team-message-container {
          position: relative;
          z-index: 2;
        }

        .team-image-wrapper {
          position: relative;
          display: inline-block;
        }

        .team-image {
          width: 280px;
          height: 280px;
          border-radius: ${RADIUS.full};
          object-fit: cover;
          object-position: center top;
          border: 6px solid ${COLORS.primary.teal};
          box-shadow: ${SHADOWS.xl};
          animation: pulsate 3s ease-in-out infinite;
        }

        .team-image:hover {
          animation-play-state: paused;
        }

        .team-content {
          display: flex;
          align-items: center;
        }

        .team-text h3 {
          color: ${COLORS.primary.dark};
          font-size: ${TYPOGRAPHY.fontSize['2xl']};
          font-weight: ${TYPOGRAPHY.fontWeight.bold};
          margin-bottom: ${SPACING.xs};
        }

        .team-text .role {
          color: ${COLORS.primary.teal};
          font-size: ${TYPOGRAPHY.fontSize.md};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          margin-bottom: ${SPACING.lg};
          display: block;
        }

        .team-text p {
          color: ${COLORS.text.primary};
          font-size: ${TYPOGRAPHY.fontSize.md};
          line-height: ${TYPOGRAPHY.lineHeight.relaxed};
          margin-bottom: ${SPACING.md};
        }

        .expertise-badge {
          display: inline-block;
          background: ${COLORS.primary.teal};
          color: ${COLORS.text.white};
          padding: ${SPACING.xs} ${SPACING.md};
          border-radius: ${RADIUS.full};
          font-size: ${TYPOGRAPHY.fontSize.sm};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          margin-right: ${SPACING.sm};
          margin-top: ${SPACING.md};
          transition: ${TRANSITIONS.all};
        }

        .expertise-badge:hover {
          transform: translateY(-2px);
          background: ${COLORS.primary.dark};
        }

        @media (max-width: 991px) and (min-width: 768px) {
          .team-image {
            width: 240px;
            height: 240px;
            margin: 0 auto ${SPACING.xl};
            display: block;
            object-position: center top;
          }

          .team-text h3 {
            font-size: ${TYPOGRAPHY.fontSize.xl};
          }
        }

        @media (max-width: 767px) {
          .team-image {
            width: 220px;
            height: 220px;
            margin: 0 auto ${SPACING.xl};
            display: block;
            object-position: center top;
          }

          .team-text {
            text-align: center;
          }

          .team-text h3 {
            font-size: ${TYPOGRAPHY.fontSize.xl};
          }

          .team-text p {
            font-size: ${TYPOGRAPHY.fontSize.base};
          }

          .expertise-badge {
            margin-right: ${SPACING.xs};
          }
        }

        @media (max-width: 576px) {
          .team-image {
            width: 180px;
            height: 180px;
            object-position: center top;
          }

          .expertise-badge {
            font-size: ${TYPOGRAPHY.fontSize.xs};
            padding: ${SPACING.xs} ${SPACING.sm};
            margin-right: ${SPACING.xs};
            margin-top: ${SPACING.xs};
          }
        }
      `}</style>

      <section className="team-message-section py-5">
        <div className="container">
          <UnifiedCard variant="glass" style={{ padding: "40px" }}>
            <div className="row align-items-center team-content">
              <div className="col-lg-4 col-md-5 text-center mb-4 mb-md-0">
                <div className="team-image-wrapper" style={{
                  position: 'relative',
                  display: 'inline-block',
                }}>
                  <img
                    src="/img/AndrewPicture.png"
                    alt="Andrew Carter - Sales Executive at Marine Sealink"
                    className="team-image"
                    style={{
                      width: '280px',
                      height: '280px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      border: '6px solid #40a8c4',
                      boxShadow: SHADOWS.xl,
                      animation: 'pulsate 3s ease-in-out infinite',
                    }}
                  />
                </div>
              </div>

              <div className="col-lg-8 col-md-7">
                <div className="team-text" style={{ display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{
                    color: COLORS.primary.dark,
                    fontSize: TYPOGRAPHY.fontSize['2xl'],
                    fontWeight: TYPOGRAPHY.fontWeight.bold,
                    marginBottom: SPACING.xs,
                  }}>Andrew Carter</h3>
                  <span className="role" style={{
                    color: COLORS.primary.teal,
                    fontSize: TYPOGRAPHY.fontSize.md,
                    fontWeight: TYPOGRAPHY.fontWeight.semibold,
                    marginBottom: SPACING.lg,
                    display: 'block',
                  }}>Sales Executive</span>

                  <p style={{
                    color: COLORS.text.primary,
                    fontSize: TYPOGRAPHY.fontSize.md,
                    lineHeight: TYPOGRAPHY.lineHeight.relaxed,
                    marginBottom: SPACING.md,
                  }}>
                    With over a decade of experience in maritime technical supply, our team is dedicated to
                    delivering unparalleled expertise and reliability to every client. We understand
                    the critical nature of spare parts availability and the cost of unplanned vessel downtime.
                  </p>

                  <p style={{
                    color: COLORS.text.primary,
                    fontSize: TYPOGRAPHY.fontSize.md,
                    lineHeight: TYPOGRAPHY.lineHeight.relaxed,
                    marginBottom: SPACING.md,
                  }}>
                    Our commitment goes beyond simply supplying parts; we build lasting partnerships
                    based on trust, technical excellence, and responsive support. Whether you need an
                    urgent replacement part or help identifying an obsolete component, we ensure
                    your operations never face unnecessary downtime.
                  </p>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginTop: SPACING.md,
                  }}>
                    <span className="expertise-badge" style={{
                      display: 'inline-block',
                      background: COLORS.primary.teal,
                      color: COLORS.text.white,
                      padding: `${SPACING.xs} ${SPACING.md}`,
                      borderRadius: '50px',
                      fontSize: TYPOGRAPHY.fontSize.sm,
                      fontWeight: TYPOGRAPHY.fontWeight.semibold,
                      marginRight: SPACING.sm,
                      transition: TRANSITIONS.all,
                    }}>10+ Years Experience</span>
                    <span className="expertise-badge" style={{
                      display: 'inline-block',
                      background: COLORS.primary.teal,
                      color: COLORS.text.white,
                      padding: `${SPACING.xs} ${SPACING.md}`,
                      borderRadius: '50px',
                      fontSize: TYPOGRAPHY.fontSize.sm,
                      fontWeight: TYPOGRAPHY.fontWeight.semibold,
                      marginRight: SPACING.sm,
                      transition: TRANSITIONS.all,
                    }}>Expert Support</span>
                    <span className="expertise-badge" style={{
                      display: 'inline-block',
                      background: COLORS.primary.teal,
                      color: COLORS.text.white,
                      padding: `${SPACING.xs} ${SPACING.md}`,
                      borderRadius: '50px',
                      fontSize: TYPOGRAPHY.fontSize.sm,
                      fontWeight: TYPOGRAPHY.fontWeight.semibold,
                      transition: TRANSITIONS.all,
                    }}>Global Network</span>
                  </div>
                </div>
              </div>
            </div>
          </UnifiedCard>
        </div>
      </section>
    </>
  );
};

export default TeamMessage;
